import React from "react";
import LateSwapTable from "./LateSwapTable";
import { Box, Button, Tab, Tabs, Theme, createStyles, makeStyles } from "@material-ui/core";
import { MenuOption } from "../common/selectOptions";
import ExposuresTable from "./ExposuresTable";
import FavoritesTable from "./FavoritesTable";
import { Stack } from "@mui/material";
import { LateSwapLineup, LateSwapPlayer } from "./store/types";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { StyledTableWrapper } from "../common/_components";

interface PlayerGroup {
  id: string;
  players: LateSwapPlayer[];
  roiBoost: number;
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& .MuiTabs-root": {
        "& .Mui-disabled": {
          backgroundColor: "#555555 !important",
          color: "#BEBEBE",
          cursor: "pointer",
        },
      },
    },
    tab: {
      marginRight: '8px',
      borderRadius: "3px 3px 0px 0px",
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter',
    },
    tabContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 4,
      borderRadius: 3,
      color: 'white',
      width: "100%",
      maxHeight: "100%",
      height: "auto",
    },
    active: {
      fontWeight: 800,
    },
    buttonWrapper: {
      position: "absolute",
      right: 0,
      display: "flex",
      alignItems: "center",
    },
    exportButton: {
      height: 40,
      borderRadius: 3,
      backgroundColor: "#0042C1",
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "#0042C1",
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    settingsWrapper: {
      borderTop: "1px solid rgba(251,255,57, .3)",
      borderRight: "1px solid rgba(251,255,57, .3)",
      borderLeft: "1px solid rgba(251,255,57, .3)",
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      padding: 4,
    },
  })
);

interface OwnProps {
  readonly selectedContest: MenuOption<any>;
  readonly exportCsvHeader: string;
  readonly boostedPlayersList: { [key: string]: number };
  readonly boostedStackList: { [key: string]: number };
  readonly playerGroups?: PlayerGroup[];
  readonly onApiRefReady?: (apiRef: React.MutableRefObject<GridApiPro>) => void;
}

type Props = OwnProps;

const FinalStepTabs = (props: Props) => {
    const classes = useStyles();
    const { selectedContest, exportCsvHeader, boostedPlayersList, boostedStackList, playerGroups = [], onApiRefReady } = props;
    const [tabValue, setTabValue] = React.useState("lineups");
    const [filteredHashcode, setFilteredHashcode] = React.useState('');
    const [filteredLineupNumber, setFilteredLineupNumber] = React.useState('');
    const [savedLineupsWhenFiltered, setSavedLineupsWhenFiltered] = React.useState<LateSwapLineup[]>([]);

    React.useEffect(() => {
      if (filteredHashcode !== '') {
        setTabValue('lineups');
      }
    }, [filteredHashcode])

    const handleDupesClose = () => {
      setFilteredHashcode('');
      setFilteredLineupNumber('');
    }

    const handleChange = (event: React.ChangeEvent<{}>, newValue: any) => {
      setTabValue(newValue);
    };
  
    return (
        <Box
          className={classes.tabContainer}
        >
            <Tabs
            value={tabValue}
            style={{minHeight: 36, height: 36}}
            indicatorColor="primary"
            textColor="inherit"
            onChange={handleChange}
            >
                <Tab
                    value="lineups"
                    style={{minHeight: 36, height: 36}}
                    label="Late Swap Options"
                    className={`${classes.tab} ${
                    tabValue === "lineups" ? classes.active : ""
                    }`}
                  />
                <Tab
                    value="favorites"
                    style={{minHeight: 36, height: 36}}
                    label="Favorites"
                    className={`${classes.tab} ${
                    tabValue === "favorites" ? classes.active : ""
                    }`}
                  />
                <Tab
                    value="exposures"
                    style={{minHeight: 36, height: 36}}
                    label="Exposures"
                    className={`${classes.tab} ${
                    tabValue === "exposures" ? classes.active : ""
                    }`}
                  />
            </Tabs>
        <StyledTableWrapper sx={{ maxHeight: '100%'}}>
          {filteredHashcode !== '' && tabValue === 'lineups' && (
            <Stack direction='row' display='flex' alignItems='center' spacing={1} sx={{height: 35, width: '100%', backgroundColor: 'rgba(255, 255, 255, 0.2)', borderRadius: '8px 8px 0px 0px'}} >
              <Box style={{ backgroundColor: 'rgba(251, 255, 57, 0.5)', height: 18, width: 18, marginLeft: 4,}} borderRadius="100px" display="flex">
                <Box style={{color: 'white', justifyContent: 'center', alignItems: 'center', display: 'flex', fontFamily: 'sans-serif', fontWeight: 600, width: '100%'}}>
                  D
                </Box>
              </Box>
              <Box>
                Dupes of {filteredLineupNumber}
              </Box>
              <Button style={{backgroundColor: 'rgba(255, 255, 255, 0.15)', maxHeight: 22, color: 'white'}} onClick={() => handleDupesClose()}>
                Close
              </Button>
            </Stack>
          )}
          {tabValue === "lineups" && (
            <LateSwapTable
              selectedContest={selectedContest}
              exportCsvHeader={exportCsvHeader}
              boostedPlayersList={boostedPlayersList}
              boostedStackList={boostedStackList}
              setFilteredHashcode={(newHashcode: string) => setFilteredHashcode(newHashcode)}
              setSavedLineupsWhenFiltered={(savedLineups: LateSwapLineup[]) => setSavedLineupsWhenFiltered(savedLineups)}
              savedLineupsWhenFiltered={savedLineupsWhenFiltered}
              setFilteredLineupNumber={(newNumber: string) => setFilteredLineupNumber(newNumber)}
              filteredHashcode={filteredHashcode}
              filteredLineupNumber={filteredLineupNumber}
              playerGroups={playerGroups}
              onApiRefReady={onApiRefReady}
              />
          )}
          {tabValue === "favorites" && (
            <FavoritesTable 
              setSavedLineupsWhenFiltered={(savedLineups: LateSwapLineup[]) => setSavedLineupsWhenFiltered(savedLineups)}
              selectedContest={selectedContest} 
              exportCsvHeader={exportCsvHeader} 
              setFilteredHashcode={(newHashcode: string) => setFilteredHashcode(newHashcode)}
              setFilteredLineupNumber={(newNumber: string) => setFilteredLineupNumber(newNumber)}
              />
          )}
          {tabValue === "exposures" && (
            <ExposuresTable selectedContest={selectedContest} exportCsvHeader={exportCsvHeader}/>
          )}
        </StyledTableWrapper>
      </Box>
    )
}

export default FinalStepTabs;

